import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ScrollText, ShieldAlert } from 'lucide-react';
import { Button, Card, EmptyState, FormField, Select, Skeleton } from '@/ui';
import { isOrgAdmin } from '@/auth/types';
import { useSessionStore } from '@/auth/session.store';
import { fetchAuditLog, type AuditLogPage as AuditLogPageData } from './api';
import { AUDIT_ACTION_CODES, AUDIT_ACTION_LABEL_KEYS } from './labels';
import { organizationKeys } from './query-keys';
import { iamErrorMessage } from './error-message';
import styles from './Organization.module.css';

/**
 * ISLEM GECMISI (`/buro/gecmis`).
 *
 * SAYFALAMA `hasMore` UZERINDEN yurur: liste sozlesmesinde `total` YOK, yani
 * "3 / 120" gibi bir sayac cizilemez ve sayfa sayisi hesaplanamaz. Ilk sayfanin
 * `snapshotAt` degeri sonraki isteklere GERI GONDERILIR; aksi halde kaydirma
 * sirasinda yazilan yeni bir denetim kaydi pencereyi kaydirir ve ayni satir
 * iki sayfada birden gorunur.
 */

/** Sayfa parametresi — offset + ilk sayfanin zaman damgasi. */
type AuditPageParam = { offset: number; snapshotAt: string | null };

const FIRST_PAGE: AuditPageParam = { offset: 0, snapshotAt: null };

const formatDate = (value: string): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export function AuditLogPage() {
  const { t } = useTranslation();
  const snapshot = useSessionStore((state) => state.snapshot);
  const admin = isOrgAdmin(snapshot);

  const [actionCode, setActionCode] = useState<string | null>(null);

  const query = useInfiniteQuery<
    AuditLogPageData,
    Error,
    { pages: AuditLogPageData[] },
    ReturnType<typeof organizationKeys.auditLog>,
    AuditPageParam
  >({
    queryKey: organizationKeys.auditLog(actionCode),
    queryFn: ({ pageParam }) => fetchAuditLog({ ...pageParam, actionCode }),
    enabled: admin,
    initialPageParam: FIRST_PAGE,
    getNextPageParam: (lastPage) =>
      lastPage.page.hasMore
        ? {
            offset: lastPage.page.offset + lastPage.page.returned,
            snapshotAt: lastPage.page.snapshotAt,
          }
        : undefined,
  });

  if (!admin) {
    return (
      <Card padding="lg">
        <EmptyState
          tone="error"
          icon={<ShieldAlert size={28} aria-hidden="true" />}
          title={t('org.onlyAdmins')}
        />
      </Card>
    );
  }

  const rows = (query.data?.pages ?? []).flatMap((page) => page.items);

  return (
    <div className={styles.page}>
      <Card padding="md">
        <div className={styles.filterRow}>
          <FormField label={t('org.auditLog.filterAction')} className={styles.filterField}>
            <Select
              value={actionCode ?? ''}
              placeholder={t('org.auditLog.filterAll')}
              options={AUDIT_ACTION_CODES.map((code) => ({
                value: code,
                // Sabit HARITA anahtar tutar; ceviri BURADA, render sirasinda.
                label: t(AUDIT_ACTION_LABEL_KEYS[code] ?? code),
              }))}
              onChange={(event) => {
                setActionCode(event.target.value === '' ? null : event.target.value);
              }}
            />
          </FormField>
          <Button
            tone="ghost"
            onClick={() => {
              setActionCode(null);
            }}
          >
            {t('filter.clearAll')}
          </Button>
        </div>
      </Card>

      {query.isPending ? (
        <Card padding="lg">
          <Skeleton shape="block" height="240px" />
        </Card>
      ) : null}

      {query.isError ? (
        <Card padding="lg">
          <EmptyState
            tone="error"
            title={t('list.error.title')}
            description={iamErrorMessage(query.error, t)}
            action={
              <Button
                tone="neutral"
                onClick={() => {
                  void query.refetch();
                }}
              >
                {t('common.retry')}
              </Button>
            }
          />
        </Card>
      ) : null}

      {query.isSuccess && rows.length === 0 ? (
        <Card padding="lg">
          <EmptyState
            icon={<ScrollText size={28} aria-hidden="true" />}
            title={t('org.auditLog.empty')}
          />
        </Card>
      ) : null}

      {rows.length > 0 ? (
        <Card padding="lg">
          <div className={styles.auditList}>
            {rows.map((row) => {
              const labelKey = AUDIT_ACTION_LABEL_KEYS[row.actionCode];
              return (
                <div key={row.id} className={styles.auditRow}>
                  {/* Bilinmeyen kod HAM basilir: yeni bir sunucu surumu bu
                      listeyi bos gostermemeli. */}
                  <span className={styles.auditAction}>
                    {labelKey === undefined ? row.actionCode : t(labelKey)}
                  </span>
                  <span className={styles.muted}>
                    {row.actorName || t('members.unknownUser')} · {formatDate(row.createdAt)}
                  </span>
                  {row.targetEmail ? <span className={styles.muted}>{row.targetEmail}</span> : null}
                  {row.permissionCount !== null ? (
                    <span className={styles.muted}>
                      {row.permissionCount} {t('org.auditLog.permissionsGranted')}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>

          <div className={styles.loadMore}>
            {query.hasNextPage ? (
              <Button
                tone="neutral"
                loading={query.isFetchingNextPage}
                onClick={() => {
                  void query.fetchNextPage();
                }}
              >
                {t('org.auditLog.loadMore')}
              </Button>
            ) : (
              <span className={styles.muted}>{t('list.endOfList')}</span>
            )}
          </div>
        </Card>
      ) : null}
    </div>
  );
}
